import { JwtHelperService } from '@auth0/angular-jwt';

export interface CredentialPair { token: string | null; refreshToken: string | null; }
interface AcceptedCredentials { token: string; refreshToken: string; }
export interface CredentialContext extends CredentialPair { endpoint: string; current(): boolean; }

/** Per-tab asynchronous-work identity only. Never an authentication or permission source. */
export class CredentialTransitions {
  private handle = {};
  private pair: CredentialPair = {token: null, refreshToken: null};
  private endpoint = '';
  private initialized = false;
  private listeners = new Set<(token: string | null) => void>();
  private jwt = new JwtHelperService();

  constructor(private read: () => CredentialPair, private write: (pair: CredentialPair) => void,
              private getEndpoint: () => string) {}

  capture(): CredentialContext {
    this.synchronize();
    const handle = this.handle;
    return Object.freeze({...this.pair, endpoint: this.endpoint, current: () => {
      this.synchronize();
      return !!this.pair.token && this.handle === handle;
    }});
  }

  /** Public asynchronous work may start anonymously, but must not cross identity/environment changes.
   * This continuity check does not grant authentication or authorize token refresh.
   */
  captureIdentity(): {current(): boolean} {
    this.synchronize();
    const handle = this.handle;
    return Object.freeze({current: () => {
      this.synchronize();
      return this.handle === handle;
    }});
  }

  /** All login/import calls replace identity, including an identical credential reimport. */
  replace(pair: CredentialPair): void {
    this.write(pair);
    this.changed();
  }

  /** Generic credential writes/removals and clears are not evidence of refresh succession. */
  changed(): void {
    this.handle = {};
    this.pair = this.read();
    this.endpoint = this.getEndpoint();
    this.initialized = true;
    this.notify();
  }

  subscribe(listener: (token: string | null) => void): void { this.listeners.add(listener); }

  successor(context: CredentialContext): AcceptedCredentials | null {
    if (!context.current()) { return null; }
    const changed = this.pair.token !== context.token || this.pair.refreshToken !== context.refreshToken;
    return changed && this.pair.token && this.pair.refreshToken && this.validToken(this.pair.token)
      ? {token: this.pair.token, refreshToken: this.pair.refreshToken} : null;
  }

  /** Called only by the refresh operation that captured this exact predecessor. */
  accept(context: CredentialContext, data: {id: string; refreshToken: string}, onCommitted?: (token: string) => void): AcceptedCredentials {
    if (!context.current()) { throw new Error('Request session context changed'); }
    const successor = this.successor(context);
    if (successor) { return successor; }
    if (this.pair.token !== context.token || this.pair.refreshToken !== context.refreshToken) {
      throw new Error('Refresh predecessor is no longer current');
    }
    const before = this.decode(context.token);
    const after = this.decode(data?.id);
    // These are response consistency checks, never proof of a login identity.
    if (!this.validToken(data?.id) || typeof data?.refreshToken !== 'string' || !data.refreshToken
      || !before?.userId || before.userId !== after?.userId
      || before.sessionAbsoluteExp !== after?.sessionAbsoluteExp) {
      throw new Error('Invalid refresh response');
    }
    this.pair = {token: data.id, refreshToken: data.refreshToken};
    this.write(this.pair);
    this.notify();
    if (context.current() && this.pair.token && onCommitted) { onCommitted(this.pair.token); }
    return {token: data.id, refreshToken: data.refreshToken};
  }

  private synchronize(): void {
    const stored = this.read();
    const endpoint = this.getEndpoint();
    if (!this.initialized) {
      // Hydration creates a new local handle; nothing pending survives a page reload.
      this.pair = stored; this.endpoint = endpoint; this.initialized = true;
    } else if (endpoint !== this.endpoint || stored.token !== this.pair.token || stored.refreshToken !== this.pair.refreshToken) {
      this.changed();
    }
  }
  private notify(): void { this.listeners.forEach(listener => listener(this.pair.token)); }
  private decode(token: string | null): any {
    try { return token ? this.jwt.decodeToken(token) : null; } catch (_) { return null; }
  }
  private validToken(token: string | null): boolean {
    const decoded = this.decode(token);
    return !!decoded && Number.isFinite(Number(decoded.exp))
      && Number.isFinite(Number(decoded.sessionAbsoluteExp))
      && Number(decoded.sessionAbsoluteExp) > Math.floor(Date.now() / 1000) && !this.jwt.isTokenExpired(token || undefined);
  }
}

/** One flight per existing refresh path; both paths commit through the same transition owner. */
export class CredentialRefresh {
  private flight: {context: CredentialContext; waiters: Array<() => boolean>; promise: Promise<CredentialPair>} | null = null;
  constructor(private transitions: CredentialTransitions, private onCommitted?: (token: string) => void) {}

  run(lifetime: () => boolean = () => true, request?: CredentialContext): Promise<{id: string; refreshToken: string}> {
    const context = this.transitions.capture();
    const current = () => context.current() && (!request || request.current()) && lifetime();
    const changed = () => new Error('Request session context changed');
    if (!current()) { return Promise.reject(changed()); }
    const successor = request && this.transitions.successor(request);
    if (successor) { return Promise.resolve({id: successor.token, refreshToken: successor.refreshToken}); }
    if (!context.token || !context.refreshToken) { return Promise.reject(new Error('Refresh credentials unavailable')); }
    let flight = this.flight;
    if (!flight || !flight.context.current() || flight.context.token !== context.token
      || flight.context.refreshToken !== context.refreshToken || !flight.waiters.some(waiter => waiter())) {
      const waiters = [current];
      const active = () => context.current() && waiters.some(waiter => waiter());
      const promise = fetch(`${context.endpoint}auth/refresh`, {
        method: 'POST', headers: {'Content-Type': 'application/json', Authorization: `Bearer ${context.token}`},
        body: JSON.stringify({refreshToken: context.refreshToken}),
      }).then(response => {
        if (!response.ok) { throw response; }
        return response.json();
      }).then(data => {
        if (!active()) { throw changed(); }
        return this.transitions.accept(context, data, this.onCommitted);
      }).catch(error => {
        if (!active()) { throw changed(); }
        const advanced = this.transitions.successor(context);
        if (advanced) { return advanced; }
        throw error;
      });
      flight = {context, waiters, promise};
      this.flight = flight;
      const completed = flight;
      const clear = () => { if (this.flight === completed) { this.flight = null; } };
      void promise.then(clear, clear);
    } else { flight.waiters.push(current); }
    return flight.promise.then(() => {
      if (!current()) { throw changed(); }
      const accepted = this.transitions.capture();
      if (!accepted.token || !accepted.refreshToken) { throw changed(); }
      return {id: accepted.token, refreshToken: accepted.refreshToken};
    });
  }
}
